import React, { useState } from 'react';
import { getUserDetails, changePassword } from '../services/authService';
import { Button } from '../components/Button';

export const AdminProfile: React.FC = () => {
  const user = getUserDetails();

  // Estado para troca de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) {
      setFeedback({ type: 'error', message: 'Preencha ambos os campos.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ type: null, message: '' });

    try {
      await changePassword(currentPassword, newPassword);
      setFeedback({ type: 'success', message: 'Senha atualizada com sucesso!' });
      setCurrentPassword('');
      setNewPassword('');
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Falha ao trocar a senha.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-4xl mx-auto mt-8 md:mt-12 px-4 pb-12">
      {/* Container Principal */}
      <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/40 overflow-hidden relative">

        {/* Cover - Gradiente */}
        <div className="h-40 md:h-48 bg-gradient-to-tr from-primary via-primary-light to-accent relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4"></div>
        </div>

        {/* Usamos pt-8 para evitar 'embolar' o titulo, trocando as margens negativas grandes para margens controladas */}
        <div className="px-6 md:px-12 pb-10 relative">

          {/* Avatar sobreposto ao header, porém sem bater no topo do card interior */}
          <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16 md:-mt-20 mb-8 relative z-10 px-2">
            <div className="group relative w-32 h-32 md:w-36 md:h-36 shrink-0 shadow-lg rounded-full">
              <div className="absolute inset-0 bg-gradient-to-br from-accent to-primary rounded-full blur opacity-40"></div>
              <div className="w-full h-full rounded-full bg-white p-1.5 relative z-10">
                <div className="w-full h-full rounded-full bg-gradient-to-br from-primary-light to-primary flex items-center justify-center text-white text-5xl font-serif font-bold tracking-tighter shadow-inner">
                  {user.name.split(' ').map((n: any) => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              </div>
            </div>

            <div className="flex-1 pb-6 md:pb-10 text-center md:text-left mt-2 md:mt-0">
              <h2 className="text-3xl md:text-3xl font-serif text-white font-bold tracking-tight drop-shadow-sm">{user.name}</h2>
              <p className="text-white/80 font-medium text-lg mb-2">@{user.username}</p>
              <span className="inline-flex items-center px-4 py-1.5 bg-accent/10 text-accent-hover text-sm font-bold rounded-full border border-accent/20 shadow-sm mt-1">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {user.role === 'admin' ? 'Administrador do Sistema' : user.role}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {/* Card Status */}
            <div className="group p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center text-green-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Status da Conta</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                    <p className="text-gray-800 font-semibold md:text-lg">Ativo e Verificado</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Card Papel */}
            <div className="group p-6 bg-gray-50/50 rounded-2xl border border-gray-100">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">Permissão Root</p>
                  <p className="text-gray-800 font-semibold md:text-lg">Privilégio Concluído</p>
                </div>
              </div>
            </div>
          </div>

          {/* Seção de Segurança - Formulário Funcional */}
          <div className="mt-8 relative overflow-hidden rounded-2xl border border-amber-200/50 bg-gradient-to-r from-amber-50 to-orange-50/30 p-6 md:p-8">
            <h4 className="text-amber-900 font-bold text-lg mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Alteração de Senha
            </h4>

            <form onSubmit={handlePasswordChange} className="max-w-md flex flex-col gap-4 relative z-10">
              <div>
                <label className="block text-sm font-medium text-amber-900/80 mb-1">Senha Atual</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-amber-200 bg-white/60 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none transition-all"
                  placeholder="Sua senha atual"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-900/80 mb-1">Nova Senha</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-amber-200 bg-white/60 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none transition-all"
                  placeholder="Sua nova senha"
                />
              </div>

              {feedback.type && (
                <div className={`p-3 rounded-xl text-sm font-medium ${feedback.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'}`}>
                  {feedback.message}
                </div>
              )}

              <div className="mt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  variant="primary"
                  className="w-full text-white shadow-sm hover:shadow"
                >
                  {isSubmitting ? 'Atualizando...' : 'Atualizar Credenciais'}
                </Button>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
};